import {
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Divider,
  Chip,
  TableHead,
} from "@mui/material";
import { styled } from "@mui/material/styles";

export const StyledTraslados = {
  Title: styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  })),
  Subtitle: styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  })),
  Paper: styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  })),
  EmptyText: styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0),
  })),
  Tabs: styled(Tabs)(({ theme }) => ({
    marginBottom: theme.spacing(2),
  })),
  Tab: styled(Tab)({}),
  ReportsGrid: styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
    "& .MuiCard-root": {
      width: "100%",
      maxWidth: "100%",
      overflow: "visible",
    },
    "& .MuiCard-root > *": {
      overflow: "visible",
    },
  })),
  ChartPanel: styled(Box)({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }),
  DateFilters: styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    alignItems: "flex-end",
    flexShrink: 0,
    "& > *": {
      flex: "1 1 180px",
      maxWidth: 240,
    },
  })),
  ChartColumn: styled(Box)({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
    flex: 1,
  }),
  ChartWithLegend: styled(Box)({
    display: "flex",
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "visible",
    position: "relative",
    zIndex: 2,
  }),
  ChartPlot: styled(Box)({
    flex: 1,
    minWidth: 0,
    height: "100%",
    overflow: "visible",
    position: "relative",
    zIndex: 3,
  }),
  ChartLegend: styled(Box)(({ theme }) => ({
    width: 200,
    flexShrink: 0,
    maxHeight: "100%",
    overflowY: "auto",
    paddingLeft: theme.spacing(1),
  })),
  ChartLegendItem: styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 22,
    cursor: "default",
  })),
  ChartLegendSwatch: styled("span", {
    shouldForwardProp: (prop) => prop !== "symbol" && prop !== "swatchColor",
  })(({ theme, symbol, swatchColor }) => ({
    width: 10,
    height: 10,
    flexShrink: 0,
    borderRadius: symbol === "square" ? 2 : "50%",
    backgroundColor: swatchColor || theme.palette.text.secondary,
  })),
  ChartLegendLabel: styled("span")(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  })),
  ChartTooltip: styled(Box)(({ theme }) => ({
    position: "relative",
    zIndex: theme.zIndex.tooltip,
    minWidth: 180,
    maxWidth: 280,
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    pointerEvents: "none",
  })),
  ChartTooltipHeader: styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  })),
  ChartTooltipTitle: styled("span")(({ theme }) => ({
    ...theme.typography.caption,
    fontWeight: 600,
    color: theme.palette.text.primary,
  })),
  ChartTooltipLine: styled("div")(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
  })),
  ChartTooltipSliceRow: styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 20,
  })),
  ChartTooltipSliceName: styled("span")(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  })),
  ChartTooltipSliceValue: styled("span")(({ theme }) => ({
    ...theme.typography.caption,
    fontWeight: 600,
    color: theme.palette.text.primary,
    flexShrink: 0,
  })),
  ChartMetrics: styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 0.5, 1),
  })),
  ChartMetric: styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    minWidth: 92,
    padding: theme.spacing(0.5, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  })),
  ChartMetricLabel: styled("span")(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  })),
  ChartMetricValue: styled("span")(({ theme }) => ({
    ...theme.typography.subtitle2,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  })),
};

export const StyledTrasladosDialog = {
  Header: styled(DialogTitle, {
    shouldForwardProp: (prop) => prop !== "bgcolor",
  })(({ theme, bgcolor }) => ({
    backgroundColor: bgcolor || "#4a90e8",
    color: "#ffffff",
    textAlign: "center",
    fontWeight: 500,
    position: "relative",
    fontSize: "14.5px",
    letterSpacing: "0.5px",
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    fontFamily: "Roboto, sans-serif",
  })),
  CloseButton: styled(IconButton)(({ theme }) => ({
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: "#ffffff",
  })),
  Content: styled(DialogContent)(() => ({
    padding: "20px 28px 16px",
    fontFamily: "Roboto, sans-serif",
  })),
  Actions: styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  })),
  SectionHeader: styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  })),
  SectionTitle: styled(Typography)(() => ({
    color: "#5b9bf5",
    fontSize: "16px",
    fontWeight: 500,
    fontFamily: "Roboto, sans-serif",
  })),
  SectionDivider: styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: "#5b9bf5",
    height: 2,
  })),
  FieldLabel: styled(Typography)(() => ({
    color: "#8a949e",
    fontSize: "0.875rem",
    fontFamily: "Roboto, sans-serif",
  })),
  FieldValue: styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    fontFamily: "Roboto, sans-serif",
  })),
  ChipsContainer: styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  })),
  StatusChip: styled(Chip, {
    shouldForwardProp: (prop) => prop !== "chipColor",
  })(({ chipColor }) => ({
    backgroundColor: chipColor || "#e0e0e0",
    color: "#ffffff",
    fontWeight: 500,
    fontSize: "12px",
  })),
  SentidoChip: styled(Chip)(() => ({
    color: "#4a90e8",
    borderColor: "#4a90e8",
    fontWeight: 500,
    fontSize: "12px",
  })),
  PosibleNegativoChip: styled(Chip)(() => ({
    backgroundColor: "#ffecb3",
    color: "#f57f17",
    fontWeight: 500,
    fontSize: "12px",
  })),
  ObservationBox: styled(Typography)(({ theme }) => ({
    backgroundColor: "#f9f9f9",
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #e0e0e0",
    fontSize: "0.875rem",
    fontFamily: "Roboto, sans-serif",
  })),
  PrimaryButton: styled(Button)(() => ({
    backgroundColor: "#4a90e8",
    color: "#ffffff",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#3b7dd8",
    },
  })),
  DangerButton: styled(Button)(() => ({
    backgroundColor: "#d94040",
    color: "#ffffff",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#b72828",
    },
  })),
  DarkRedButton: styled(Button)(() => ({
    backgroundColor: "#8b0000",
    color: "#ffffff",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#5c0000",
    },
  })),
};

export const StyledTrasladosTable = {
  DetailPanel: styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: "#f7f9fb",
  })),
  DetailTableHead: styled(TableHead)(() => ({
    backgroundColor: "#eef2f6",
  })),
  ActionGroup: styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(0.5),
  })),
  DarkRedIconButton: styled(IconButton)(() => ({
    color: "#8b0000",
  })),
  ResumenChip: styled(Chip)(() => ({
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
  })),
  PosibleNegativoChip: styled(Chip)(() => ({
    backgroundColor: "#ffecb3",
    color: "#f57f17",
    fontWeight: "bold",
  })),
};

StyledTraslados.Dialog = StyledTrasladosDialog;
StyledTraslados.Table = StyledTrasladosTable;

